import { useEffect, useState } from "react"
import { AuthModel } from "../models/auth.model"
import { AuthService } from "../services/auth.service"
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts"
import { ChartArea } from "lucide-react"

// One room's daily occupancy share across the requested date range.
interface RoomSeries {
    name: string
    color: string
    values: number[]
}

interface HistoryResponse {
    status: string
    dates: string[]
    rooms: RoomSeries[]
}

export interface LocationHistoryChartProps {
    deploymentId: number
    date: string
    toDate: string
}

// Stacked-area room-occupancy trend shown below the Location Report map.
// Backed by the well-api get_location_history_data endpoint, which returns the
// same per-day room percentages encoded in the map's bar strip. Clicking a
// room (area or legend chip) isolates it — others flatten to zero — with
// Show all / Prev / Next, mirroring the History.html chart.
export function LocationHistoryChart(props: LocationHistoryChartProps) {
    const [dates, setDates] = useState<string[]>([])
    const [rooms, setRooms] = useState<RoomSeries[]>([])
    const [selectedRoom, setSelectedRoom] = useState<string | null>(null)
    const [errorMessage, setErrorMessage] = useState<string>()
    const [loading, setLoading] = useState<boolean>(true)

    useEffect(() => {
        setLoading(true)
        setErrorMessage(undefined)
        let cancelled = false
        const auth: AuthModel = AuthService.getAuth()
        const url = `${process.env.REACT_APP_API_BASE}/function/well-api/api?name=get_location_history_data`
            + `&deployment_id=${props.deploymentId}&date=${props.date}&to_date=${props.toDate}`
            + `&filter=5&user_name=${auth.username}&token=${auth.token}`
        fetch(url)
            .then(r => r.ok ? r.json() : Promise.reject(new Error("request failed")))
            .then((data: HistoryResponse) => {
                if (cancelled) return
                if (data.status !== "200 OK" || !Array.isArray(data.rooms))
                    throw new Error("history unavailable")
                setDates(data.dates || [])
                setRooms(data.rooms || [])
                setSelectedRoom(null)
                setLoading(false)
            })
            .catch(() => {
                if (cancelled) return
                setErrorMessage("Could not load room-occupancy history")
                setLoading(false)
            })
        return () => { cancelled = true }
    }, [props.deploymentId, props.date, props.toDate])

    // One row per day; an isolated room keeps its values, the rest go to zero
    // so the stack collapses to just that band.
    const chartData = dates.map((d, i) => {
        const row: { [k: string]: string | number } = { date: d.slice(5) }
        rooms.forEach(r => {
            row[r.name] = (selectedRoom && r.name !== selectedRoom) ? 0 : (r.values[i] ?? 0)
        })
        return row
    })

    function toggleRoom(name: string) {
        setSelectedRoom(prev => (prev === name ? null : name))
    }

    function stepRoom(delta: number) {
        if (rooms.length === 0) return
        const idx = selectedRoom ? rooms.findIndex(r => r.name === selectedRoom) : -1
        setSelectedRoom(rooms[(idx + delta + rooms.length) % rooms.length].name)
    }

    const selectedAvg = (() => {
        if (!selectedRoom) return null
        const r = rooms.find(x => x.name === selectedRoom)
        if (!r || r.values.length === 0) return null
        return Math.round(r.values.reduce((a, b) => a + b, 0) / r.values.length)
    })()

    return (
        <div className="border-t pt-4 mt-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center mb-3">
                <ChartArea className="mr-2 text-gray-600 bg-gray-100 p-2 rounded-lg" size={32} />
                Room Occupancy Over Time
            </h3>

            {errorMessage ? (
                <div className="text-center text-red-500 py-6">{errorMessage}</div>
            ) : loading ? (
                <div className="text-center text-gray-600 py-6">Loading chart data... Please wait!</div>
            ) : chartData.length < 2 ? (
                <div className="text-center text-gray-600 py-6">
                    Select Week, 1Mo or 6Mo to see the room-occupancy trend.
                </div>
            ) : (
                <div>
                    <ResponsiveContainer width="100%" height={260}>
                        <AreaChart data={chartData} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="date" tick={{ fontSize: 11 }} tickLine={false} minTickGap={16} />
                            <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`}
                                tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={44} />
                            <Tooltip formatter={(v: number | string) => `${v}%`} />
                            {rooms.map(r => (
                                <Area
                                    key={r.name}
                                    type="monotone"
                                    dataKey={r.name}
                                    stackId="rooms"
                                    stroke={r.color}
                                    fill={r.color}
                                    fillOpacity={selectedRoom && r.name !== selectedRoom ? 0.06 : 0.75}
                                    onClick={() => toggleRoom(r.name)}
                                    style={{ cursor: "pointer" }}
                                />
                            ))}
                        </AreaChart>
                    </ResponsiveContainer>

                    {/* Clickable legend — isolate a room */}
                    <div className="flex flex-wrap gap-2 mt-3">
                        {rooms.map(r => (
                            <button
                                key={r.name}
                                type="button"
                                onClick={() => toggleRoom(r.name)}
                                className={"flex items-center gap-1 px-2 py-1 rounded text-xs border cursor-pointer "
                                    + (selectedRoom === r.name ? "border-gray-800 font-semibold" : "border-gray-200")}
                                style={{ opacity: selectedRoom && selectedRoom !== r.name ? 0.4 : 1 }}
                            >
                                <span className="inline-block w-3 h-3 rounded-sm" style={{ background: r.color }} />
                                {r.name}
                            </button>
                        ))}
                    </div>

                    <div className="flex items-center justify-center gap-3 mt-3 text-sm">
                        <button type="button" className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 cursor-pointer"
                            onClick={() => stepRoom(-1)}>&lt; Prev</button>
                        <button type="button" className="px-3 py-1 rounded bg-blue-500 text-white cursor-pointer"
                            onClick={() => setSelectedRoom(null)}>Show all</button>
                        <button type="button" className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 cursor-pointer"
                            onClick={() => stepRoom(1)}>Next &gt;</button>
                    </div>

                    {selectedRoom &&
                        <div className="text-center text-gray-700 mt-2 text-sm">
                            {selectedRoom} — {selectedAvg}% average over this period
                        </div>
                    }
                </div>
            )}
        </div>
    )
}
